import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const ManageAttendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [uniqueStudents, setUniqueStudents] = useState([]);
  const [viewMode, setViewMode] = useState("attendance");
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    studentName: "",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();

  const fetchAttendance = async (query = "") => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/all?page=${page}&limit=${limit}${query}`
      );
      const result = await response.json();
      setAttendanceData(Array.isArray(result.data) ? result.data : []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueStudents = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/unique-students`
      );
      const result = await response.json();
      console.log("👀 Unique students response:", result);
      setUniqueStudents(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      console.error("Error fetching unique students:", err);
      setUniqueStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (viewMode === "attendance") {
      handleSearch();
    } else {
      fetchUniqueStudents();
    }
  }, [page, limit, viewMode]);

  const handleSearch = () => {
    const { dateFrom, dateTo, studentName } = filters;
    const queryParams = new URLSearchParams();

    if (dateFrom) queryParams.append("dateFrom", dateFrom);
    if (dateTo) queryParams.append("dateTo", dateTo);
    if (studentName) queryParams.append("studentName", studentName.trim());

    const queryString = queryParams.toString()
      ? `&${queryParams.toString()}`
      : "";
    fetchAttendance(queryString);
  };

  const handleClear = () => {
    setFilters({ dateFrom: "", dateTo: "", studentName: "" });
    setPage(1);
    fetchAttendance();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const exportUniqueToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Name", "Email", "Phone"]],
      body: uniqueStudents.map((student) => [
        student.name,
        student.email,
        student.phone,
      ]),
    });
    doc.save("unique_students.pdf");
  };

  const exportUniqueToExcel = () => {
    const csvContent = [
      ["Name", "Email", "Phone"],
      ...uniqueStudents.map((student) => [
        student.name,
        student.email,
        student.phone,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "unique_students.csv");
  };

  return (
    <>
      <header>
        <div className="homebanner">
          <img src="homebanner.png" alt="HomePage Banner" />
        </div>
        <div className="homelogo">
          <img src="church_logo_no_bg.png" alt="Church Logo" />
        </div>
        <div className="cross_icon">
          <img src="cross_icon.png" alt="Cross Icon" />
        </div>
      </header>

      <footer>
        <div className="homebody">
          <button
            onClick={() => navigate("/HomePage")}
            className="mb-4 text-blue-600 hover:underline"
          >
            ← Back to Dashboard
          </button>

          <div className="managebody">
            <h1>Attendance</h1>
            <button
              onClick={() =>
                setViewMode(viewMode === "attendance" ? "unique" : "attendance")
              }
              className="ml-4 px-4 py-2 rounded bg-indigo-600 text-white"
            >
              {viewMode === "attendance"
                ? "View Unique Students"
                : "Back to Attendance Log"}
            </button>
          </div>

          {viewMode === "unique" ? (
            <>
              {loading ? (
                <p>Loading unique students...</p>
              ) : uniqueStudents.length === 0 ? (
                <p>No unique students found.</p>
              ) : (
                <>
                  <div className="table-wrapper">
                    <table className="student-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueStudents.map((student, index) => (
                          <tr key={index}>
                            <td>{student.name}</td>
                            <td>{student.email}</td>
                            <td>{student.phone}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="export-buttons mt-4">
                    <button
                      onClick={exportUniqueToPDF}
                      className="mr-2 bg-blue-500 text-white px-4 py-2 rounded"
                    >
                      Export to PDF
                    </button>
                    <button
                      onClick={exportUniqueToExcel}
                      className="bg-green-500 text-white px-4 py-2 rounded"
                    >
                      Export to Excel
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <form
                className="filter-section"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPage(1);
                  handleSearch();
                }}
              >
                <input
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleChange}
                />
                <input
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="studentName"
                  placeholder="Student Name"
                  value={filters.studentName}
                  onChange={handleChange}
                />
                <button type="submit">Search</button>
                <button type="button" onClick={handleClear}>
                  Clear
                </button>
              </form>

              <div className="table-wrapper">
                {loading ? (
                  <p>Loading attendance...</p>
                ) : attendanceData.length === 0 ? (
                  <p>No attendance found.</p>
                ) : (
                  <table className="student-table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Student Email</th>
                        <th>Phone Number</th>
                        <th>Time and Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceData.map((entry, index) => (
                        <tr key={index}>
                          <td>{entry.name}</td>
                          <td>{entry.email}</td>
                          <td>{entry.phone}</td>
                          <td>{new Date(entry.timestamp).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="pagination">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={page === i + 1 ? "active" : ""}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  &gt;
                </button>
              </div>

              <div className="ml-4 mt-4">
                <label htmlFor="limit" className="mr-2">
                  Rows per page:
                </label>
                <select
                  id="limit"
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </>
          )}
        </div>
      </footer>
    </>
  );
};

export default ManageAttendance;
